/* The erase-account card — only what a CONFIRM has no equivalent for.
 *
 * The backdrop, the popup, the title, the description, the actions row and the two answer treatments
 * are `shared/ui/dialogSurface.module.css`'s, imported alongside this one. Every one of them used to
 * be transcribed here declaration for declaration, which is the near-duplicate the house rule exists
 * to prevent; promoting the surface on 18 September 2026 made this its third consumer.
 *
 * The card's WIDTH is the one thing about the box that is this screen's — two columns of
 * consequences do not fit 520 — and it is handed to the surface through `--dialog-w` rather than
 * written as a second `width`, so no declaration is settled by chunk order.
 *
 * Every colour, space, radius and type value is a token; heights and widths are layout. */

/* Read by `dialogSurface`'s `.popup`; nothing here declares a width of its own. */
.wide {
  --dialog-w: 580px;
}

/* The blocks below are the mockup's `#dlg-erase` and `_blocks.css`'s `.fate` / `.notice`. */

/* ── What goes and what stays ─────────────────────────────────────────────────────────────── */

.fate {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: var(--space-3);
}

.fateBox {
  padding: var(--space-3) var(--space-14px);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-12px);
}

.fateTitle {
  margin-bottom: var(--space-1-5px);
  font-size: var(--fs-micro);
  font-weight: var(--fw-extrabold);
  color: var(--text);
}

/* The column that lists what is destroyed, titled in the danger INK — `--danger` itself is a
 * non-text value, and this is words. */
.gone .fateTitle {
  color: var(--danger-text-strong);
}

.fateList {
  display: grid;
  gap: var(--space-1);
  padding-left: var(--space-14px);
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  line-height: var(--lh-hint);
  color: var(--text-2);
  list-style: disc;
}

/* ── The subscription notice ──────────────────────────────────────────────────────────────── */

/* Neutral, not warn-coloured: it is a DISCLOSURE, not a refusal and not a warning about this act.
 * Colouring it would read as „you cannot do this yet", which is the one thing it must never say. */
.notice {
  display: flex;
  gap: var(--space-2-5px);
  padding: var(--space-3) var(--space-14px);
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  line-height: var(--lh-note);
  color: var(--text-2);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-12px);
}

.noticeIcon {
  display: block;
  flex: none;
  width: var(--sz-account-glyph-md);
  height: var(--sz-account-glyph-md);
  margin-top: var(--space-1px);
  color: var(--text-2);
}

/* Sized so the three answers — the wait, the vague one and the named store — stand in one box, and
 * the button under them cannot move while the entitlement is still in flight. */
.noticeText {
  flex: 1;
  min-width: 0;
  min-height: var(--h-account-notice);
}

.noticeTitle {
  display: block;
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  color: var(--text);
}

.noticeBarTitle {
  display: block;
  width: 56%;
  height: var(--fs-label);
  margin-bottom: var(--space-1-5px);
}

.noticeBarBody {
  display: block;
  width: 88%;
  height: var(--fs-micro);
}

.noticeLink {
  font-weight: var(--fw-bold);
  color: var(--pop-blue);
  text-decoration: underline;
  text-underline-offset: var(--space-3px);
}

/* ── The typed word ───────────────────────────────────────────────────────────────────────── */

.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-1-5px);
}

.fieldLabel {
  font-size: var(--fs-micro);
  font-weight: var(--fw-bold);
  color: var(--text-2);
}

.input {
  height: var(--h-control);
  padding: var(--space-0) var(--space-3);
  font-family: var(--font-body);
  font-size: var(--fs-dialog-field);
  font-weight: var(--fw-semibold);
  color: var(--text);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--r-field);
}

.input:focus-visible {
  border-color: var(--text);
  outline: none;
  box-shadow: var(--ring-field);
}

.error {
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  color: var(--danger-text-strong);
}

